import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  ActionIcon
} from '@mantine/core'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import agent from '../../app/api/agent'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { AuthGateCta } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import type { Part, PartFormValues } from '../../app/models/part'
import PartFormModal from '../inventory/PartFormModal'
import CommunityPhotosGallery from '../contentPhotos/CommunityPhotosGallery'

function formatCurrency (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

export default observer(function PartDetail () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore, voteStore, partStore } = useStore()
  const { updatePart, deletePart, loading: partStoreLoading } = partStore

  const [part, setPart] = useState<Part | null>(null)
  const [loading, setLoading] = useState(true)
  const [editingPart, setEditingPart] = useState<Part | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const loadPart = useCallback(() => {
    if (!id || !userStore.isLoggedIn) {
      setPart(null)
      setLoading(false)
      return
    }
    setLoading(true)
    agent.Parts.details(id)
      .then(setPart)
      .catch(() => setPart(null))
      .finally(() => setLoading(false))
  }, [id, userStore.isLoggedIn])

  useEffect(() => {
    loadPart()
  }, [loadPart])

  const handlePartVote = async (value: number) => {
    if (!part) return
    const isOwn = userStore.user?.username === part.authorUsername
    if (isOwn) return
    try {
      if (part.currentUserVote === value) {
        await agent.Votes.remove(agent.VoteTargetType.Part, part.id)
      } else {
        await agent.Votes.cast(agent.VoteTargetType.Part, part.id, value)
      }
      loadPart()
    } catch {
      /* interceptor */
    }
  }

  const handleUpdateStandalone = async (partId: string, values: PartFormValues) => {
    await updatePart(partId, values)
    setEditingPart(null)
    loadPart()
  }

  const handleConfirmDelete = async () => {
    if (!part) return
    try {
      await deletePart(part.id)
      toast.success('Part deleted.')
      history.push('/parts-bin')
    } catch {
      /* interceptor */
    } finally {
      setDeleteOpen(false)
    }
  }

  if (!userStore.isLoggedIn) {
    return (
      <Box py="xl">
        <AuthGateCta action="view part details" pitch="Sign in to open this part and community photos." />
      </Box>
    )
  }

  if (loading) {
    return <LoadingComponent content="Loading part…" />
  }

  if (!part) {
    return (
      <Box py="xl">
        <Text>Part not found or you do not have access.</Text>
        <Button mt="md" variant="subtle" onClick={() => history.push('/parts-bin')}>
          Back to Parts Bin
        </Button>
      </Box>
    )
  }

  const platformChips =
    part.compatiblePlatforms
      ?.split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean) ?? []

  return (
    <Box>
      <Card
        padding="lg"
        radius="md"
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          marginBottom: 24
        }}
      >
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]} spacing="lg">
          <Box>
            {part.imageUrl
              ? (
              <Box
                component="img"
                src={part.imageUrl}
                alt=""
                sx={{
                  width: '100%',
                  maxHeight: 320,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid var(--gs-border)'
                }}
              />
                )
              : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: 200,
                  borderRadius: 8,
                  border: '1px dashed var(--gs-border)',
                  background: 'var(--gs-surface2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16
                }}
              >
                <Text align="center" color="dimmed" weight={600}>
                  {part.name}
                </Text>
              </Box>
                )}
          </Box>
          <Stack spacing="sm">
            <Text
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 28,
                lineHeight: 1.2
              }}
            >
              {part.name}
            </Text>
            {(part.manufacturer || part.partNumber) && (
              <Text size="sm" color="dimmed">
                {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
              </Text>
            )}
            <Text size="xl" weight={700} sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(part.value)}
            </Text>
            <Group spacing="xs">
              {part.condition && (
                <Badge size="sm" variant="light" radius="xl">
                  {part.condition}
                </Badge>
              )}
              {part.category && (
                <Badge size="sm" variant="outline" radius="sm">
                  {part.category}
                </Badge>
              )}
            </Group>
            {platformChips.length > 0 && (
              <Group spacing="xs">
                {platformChips.map((p) => (
                  <Badge key={p} size="sm" variant="dot" radius="sm">
                    {p}
                  </Badge>
                ))}
              </Group>
            )}
            <Group spacing="xs" mt="xs">
              {userStore.user?.username !== part.authorUsername && (
                <>
                  <ActionIcon
                    size="sm"
                    variant={part.currentUserVote === 1 ? 'filled' : 'subtle'}
                    color="gray"
                    onClick={() => void handlePartVote(1)}
                    disabled={voteStore.loading}
                  >
                    <FiChevronUp size={14} />
                  </ActionIcon>
                  <Text size="sm" weight={500} sx={{ minWidth: 16, textAlign: 'center' }}>
                    {part.voteScore ?? 0}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant={part.currentUserVote === -1 ? 'filled' : 'subtle'}
                    color="gray"
                    onClick={() => void handlePartVote(-1)}
                    disabled={voteStore.loading}
                  >
                    <FiChevronDown size={14} />
                  </ActionIcon>
                </>
              )}
              {userStore.user?.username === part.authorUsername && (part.voteScore ?? 0) !== 0 && (
                <Text size="sm" color="dimmed">
                  Score: {part.voteScore ?? 0}
                </Text>
              )}
            </Group>
          </Stack>
        </SimpleGrid>
      </Card>

      <Card
        padding="lg"
        radius="md"
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          marginBottom: 24
        }}
      >
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
          Details
        </Text>
        {part.description
          ? (
          <Text size="sm" sx={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            {part.description}
          </Text>
            )
          : (
          <Text size="sm" color="dimmed" mb="sm">
            No description.
          </Text>
            )}
        {part.notes
          ? (
          <Text size="sm" color="dimmed" sx={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            <Text component="span" weight={600} color="var(--gs-text)">
              Notes:{' '}
            </Text>
            {part.notes}
          </Text>
            )
          : null}
        <Text size="sm" color="dimmed" mb="sm">
          Used in{' '}
          <Text component="span" weight={600} color="var(--gs-text)">
            {part.usedInBuildsCount ?? 0}
          </Text>{' '}
          build{(part.usedInBuildsCount ?? 0) === 1 ? '' : 's'}
        </Text>
        {(part.firearmModelNames?.length ?? 0) > 0 && (
          <Box>
            <Text size="xs" color="dimmed" mb={6}>
              Compatible firearm models
            </Text>
            <Group spacing="xs">
              {part.firearmModelNames!.map((n) => (
                <Badge key={n} size="sm" variant="light" radius="sm">
                  {n}
                </Badge>
              ))}
            </Group>
          </Box>
        )}
      </Card>

      {id && <CommunityPhotosGallery entityType="Part" entityId={id} />}

      {userStore.isAdmin && (
        <Group mt="xl" spacing="sm">
          <Button variant="light" onClick={() => setEditingPart(part)}>
            Edit
          </Button>
          <Button color="red" variant="outline" onClick={() => setDeleteOpen(true)}>
            Delete
          </Button>
        </Group>
      )}

      <PartFormModal
        opened={!!editingPart}
        onClose={() => setEditingPart(null)}
        onSave={() => {}}
        mode="standalone"
        initialPartFromBin={editingPart}
        onUpdateStandalone={handleUpdateStandalone}
        loading={partStoreLoading}
      />

      <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete part">
        <Text size="sm" mb="md">
          Delete this part permanently?
        </Text>
        <Group position="right">
          <Button variant="default" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={() => void handleConfirmDelete()}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Box>
  )
})
